import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Person as PersonIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';

const COBRADOR_STATUSES = [
  { id: "activo", name: "Activo", color: "success" },
  { id: "inactivo", name: "Inactivo", color: "default" },
  { id: "suspendido", name: "Suspendido", color: "error" }
];

export default function CrearCobradorDialog({ open, onClose, onConfirm, cobrador = null }) {
  const [formData, setFormData] = useState({
    nombre: '',
    identificacion: '',
    telefono: '',
    email: '',
    direccion: '',
    usuario: '',
    contrasena: '',
    estado: 'activo',
    comision_porcentaje: 0.00,
    notas: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [messageAlert, setMessageAlert] = useState('');

  const isEdit = !!cobrador;

  useEffect(() => {
    if (open && cobrador) {
      setFormData({
        nombre: cobrador.nombre || '',
        identificacion: cobrador.identificacion || '',
        telefono: cobrador.telefono || '',
        email: cobrador.email || '',
        direccion: cobrador.direccion || '',
        usuario: cobrador.usuario || '',
        contrasena: cobrador.contrasena || '',
        estado: cobrador.estado || 'activo',
        comision_porcentaje: cobrador.comision_porcentaje || 0.00,
        notas: cobrador.notas || ''
      });
      setErrors({});
    } else if (open && !cobrador) {
      setFormData({
        nombre: '',
        identificacion: '',
        telefono: '',
        email: '',
        direccion: '',
        usuario: '',
        contrasena: '',
        estado: 'activo',
        comision_porcentaje: 0.00,
        notas: ''
      });
      setErrors({});
    }
  }, [open, cobrador]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nombre || !formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.identificacion || !formData.identificacion.trim()) newErrors.identificacion = 'La identificación es requerida';
    if (!formData.usuario || !formData.usuario.trim()) newErrors.usuario = 'El usuario es requerido';
    if (!formData.contrasena || !formData.contrasena.trim()) newErrors.contrasena = 'La contraseña es requerida';
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }
    if (formData.comision_porcentaje < 0 || formData.comision_porcentaje > 100) {
      newErrors.comision_porcentaje = 'La comisión debe estar entre 0 y 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setMessageAlert('Por favor, corrige los errores en el formulario.');
      setOpenAlert(true);
      return;
    }

    setLoading(true);
    try {
      const url = isEdit 
        ? `${process.env.REACT_APP_SERVER_URL}/api/cobradores/${cobrador.id}`
        : `${process.env.REACT_APP_SERVER_URL}/api/cobradores`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      const requestData = {
        ...formData,
        idcompany: localStorage.getItem('idcompany'),
        created_by: localStorage.getItem('id')
      };

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        setMessageAlert(isEdit ? 'Cobrador actualizado exitosamente.' : 'Cobrador creado exitosamente.');
        setOpenAlert(true);
        onConfirm(isEdit ? { ...cobrador, ...formData } : result);
        onClose();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Error al procesar la solicitud');
      }
    } catch (error) {
      setMessageAlert(error.message || 'Error al procesar la solicitud.');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      nombre: '',
      identificacion: '',
      telefono: '',
      email: '',
      direccion: '',
      usuario: '',
      contrasena: '',
      estado: 'activo',
      comision_porcentaje: 0.00,
      notas: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                backgroundColor: 'primary.main',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}
            >
              {isEdit ? <EditIcon sx={{ fontSize: 24 }} /> : <PersonIcon sx={{ fontSize: 24 }} />}
            </Box>
            <Typography variant="h6" fontWeight="bold">
              {isEdit ? 'Editar Cobrador' : 'Nuevo Cobrador'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            {/* Nombre */}
            <Grid item xs={12} md={6}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Nombre Completo *
              </Typography>
              <TextField
                fullWidth
                value={formData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                error={!!errors.nombre}
                helperText={errors.nombre}
                placeholder="Ingrese el nombre completo"
              />
            </Grid>

            {/* Identificación */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Identificación *
              </Typography>
              <TextField
                fullWidth
                value={formData.identificacion}
                onChange={(e) => handleInputChange('identificacion', e.target.value)}
                error={!!errors.identificacion}
                helperText={errors.identificacion}
                placeholder="Cédula, Pasaporte, etc."
              />
            </Grid>

            {/* Teléfono */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Teléfono
              </Typography>
              <TextField
                fullWidth
                value={formData.telefono}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
                placeholder="Número de teléfono"
                InputProps={{
                  startAdornment: <InputAdornment position="start">+1</InputAdornment>,
                }}
              />
            </Grid>

            {/* Email */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Email
              </Typography>
              <TextField
                fullWidth
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                placeholder="correo@ejemplo.com"
              />
            </Grid>

            {/* Dirección */}
            <Grid item size={{ xs: 12 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Dirección
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={formData.direccion}
                onChange={(e) => handleInputChange('direccion', e.target.value)}
                placeholder="Dirección completa del cobrador"
              />
            </Grid>

            {/* Usuario */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Usuario *
              </Typography>
              <TextField
                fullWidth
                value={formData.usuario}
                onChange={(e) => handleInputChange('usuario', e.target.value)}
                error={!!errors.usuario}
                helperText={errors.usuario}
                placeholder="Nombre de usuario para login"
              />
            </Grid>

            {/* Contraseña */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Contraseña *
              </Typography>
              <TextField
                fullWidth
                type="password"
                value={formData.contrasena}
                onChange={(e) => handleInputChange('contrasena', e.target.value)}
                error={!!errors.contrasena}
                helperText={errors.contrasena}
                placeholder="Contraseña para login"
              />
            </Grid>

            {/* Estado */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Estado
              </Typography>
              <FormControl fullWidth>
                <Select
                sx={{
                  backgroundColor: 'white'
                }}
                  value={formData.estado}
                  onChange={(e) => handleInputChange('estado', e.target.value)}
                >
                  {COBRADOR_STATUSES.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip 
                          label={status.name} 
                          color={status.color} 
                          size="small"
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Comisión */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Comisión (%)
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={formData.comision_porcentaje}
                onChange={(e) => handleInputChange('comision_porcentaje', parseFloat(e.target.value) || 0)}
                error={!!errors.comision_porcentaje}
                helperText={errors.comision_porcentaje}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>

            {/* Notas */}
            <Grid item size={{ xs: 12 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Notas
              </Typography>
              <TextField
                fullWidth
                multiline
                value={formData.notas}
                onChange={(e) => handleInputChange('notas', e.target.value)}
                placeholder="Notas adicionales sobre el cobrador..."
              />
            </Grid>
          </Grid>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ fontWeight: 'bold' }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            color="primarybutton"
            sx={{ 
              fontWeight: 'bold'
            }}
          >
            {loading ? (isEdit ? 'Actualizando...' : 'Creando...') : (isEdit ? 'Actualizar Cobrador' : 'Crear Cobrador')}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessageDialog 
        message={messageAlert} 
        title="" 
        severity="success" 
        color="success" 
        open={openAlert} 
        handleClose={() => setOpenAlert(false)}
      />
    </>
  );
}
